import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

// Log one incident or merit. Categories come from SystemLookups
// ('BehaviorCategory' — manageable via the Lists admin).
const BehaviorIncidentModal = ({ apiCall, year, className, sectionCode, onClose, onSaved, showSuccess, showError }) => {
  const [students, setStudents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    studentId: '', recordDate: new Date().toISOString().slice(0, 10),
    recordType: 'Incident', category: '', description: '', actionTaken: ''
  });

  useEffect(() => {
    let url = `/academic/roster?academicYear=${year}&className=${encodeURIComponent(className)}`;
    if (sectionCode) url += `&section=${encodeURIComponent(sectionCode)}`;
    apiCall(url).then(setStudents).catch(() => {});
    apiCall('/registration/lookups').then(l => setCategories(l.BehaviorCategory || [])).catch(() => {});
  }, [apiCall, year, className, sectionCode]);

  const save = async () => {
    if (!form.studentId) { showError('Pick a student'); return; }
    try {
      await apiCall('/academic/behavior', {
        method: 'POST',
        body: JSON.stringify({ academicYear: year, ...form })
      });
      showSuccess(`${form.recordType} logged`);
      onSaved();
    } catch (err) { showError(err.message); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">New Behavior Record — {className} {sectionCode}</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="space-y-3">
          <div className="flex gap-2">
            <button
              onClick={() => setForm(f => ({ ...f, recordType: 'Incident' }))}
              className={`flex-1 py-2 rounded-lg text-sm font-medium ${form.recordType === 'Incident' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600'}`}
            >Incident</button>
            <button
              onClick={() => setForm(f => ({ ...f, recordType: 'Merit' }))}
              className={`flex-1 py-2 rounded-lg text-sm font-medium ${form.recordType === 'Merit' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600'}`}
            >Merit</button>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Student *</label>
            <select value={form.studentId} onChange={e => setForm(f => ({ ...f, studentId: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm">
              <option value="">— student —</option>
              {students.map(s => <option key={s.StudentID} value={s.StudentID}>{s.LastName} {s.FirstName} {s.Section ? `(${s.Section})` : ''}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Date</label>
              <input type="date" value={form.recordDate} onChange={e => setForm(f => ({ ...f, recordDate: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Category</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="">— none —</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Action taken</label>
            <input type="text" value={form.actionTaken} onChange={e => setForm(f => ({ ...f, actionTaken: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <button onClick={save} className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Save Record
          </button>
        </div>
      </div>
    </div>
  );
};

export default BehaviorIncidentModal;
